import { AlertTriangle, Check, ChevronLeft, RefreshCw, X } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { terug } from '../lib/router'
import { useStaat, wisMelding } from '../lib/staat'

/* ===========================================================================
 *  De bouwstenen: de kopbalk met "‹ Terug", een regel in een lijst, een
 *  kiezer die als eigen scherm opent, en de melding onderin.
 *
 *  Zoals de telefoonapp van Blue10, die de administratie kent: grijze balk,
 *  blauwe terugknop met de naam van het vorige scherm, titel in het midden.
 * =========================================================================== */

export function Kop({ terugNaar, terugLabel, titel, ondertitel, rechts, opTerug }: {
  terugNaar?: string
  terugLabel?: string
  titel: string
  ondertitel?: string
  rechts?: ReactNode
  /** In plaats van naar het vorige adres: iets sluiten (een kiezer). */
  opTerug?: () => void
}) {
  return (
    <header className="kop">
      <div className="kop-links">
        {(terugLabel || opTerug) && (
          <button className="kop-terug" onClick={() => (opTerug ? opTerug() : terug(terugNaar ?? '/'))}>
            <ChevronLeft size={30} strokeWidth={2.4} aria-hidden="true" />
            <span>{terugLabel ?? 'Terug'}</span>
          </button>
        )}
      </div>
      <div className="kop-midden">
        <h1 className="kop-titel">{titel}</h1>
        {ondertitel && <div className="kop-ondertitel">{ondertitel}</div>}
      </div>
      <div className="kop-rechts">{rechts}</div>
    </header>
  )
}

export function Ververs({ bezig, opKlik }: { bezig: boolean; opKlik: () => void }) {
  return (
    <button className="kop-knop" onClick={opKlik} disabled={bezig} aria-label="Opnieuw ophalen">
      <RefreshCw size={20} className={bezig ? 'draait' : undefined} />
    </button>
  )
}

export function Laden({ tekst = 'Even ophalen…' }: { tekst?: string }) {
  return <div className="laden"><span className="laden-stip" aria-hidden="true" />{tekst}</div>
}

export function FoutVak({ tekst, opnieuw }: { tekst: string; opnieuw?: () => void }) {
  return (
    <div className="foutvak" role="alert">
      <AlertTriangle size={18} aria-hidden="true" />
      <div>
        <div>{tekst}</div>
        {opnieuw && <button className="link" onClick={opnieuw}>Opnieuw proberen</button>}
      </div>
    </div>
  )
}

export function Leeg({ children }: { children: ReactNode }) {
  return <div className="leeg">{children}</div>
}

/**
 * Een keuzelijst die als eigen scherm opent, zoals "Gebruiker" en "Acties"
 * in Blue10: een lijst, een vinkje bij wat gekozen is, en terug.
 */
export function Kiezer<T extends string>({ titel, terugLabel, opties, gekozen, kies, sluit, leegTekst, zoekbaar }: {
  titel: string
  terugLabel: string
  opties: { waarde: T; label: string; uitleg?: string; uit?: string }[]
  gekozen: T | null
  kies: (w: T) => void
  sluit: () => void
  leegTekst?: string
  zoekbaar?: boolean
}) {
  const [filter, setFilter] = useState('')
  const zichtbaar = filter
    ? opties.filter((o) => o.label.toLowerCase().includes(filter.toLowerCase()))
    : opties

  /*
   * De terugknop (of de veeg vanaf de rand) sluit de kiezer, niet het scherm
   * eronder. Daarvoor één stap in de geschiedenis, één keer: een ref en geen
   * afhankelijkheid, want een effect dat bij elke weergave opnieuw duwt,
   * stapelt stappen op die je daarna een voor een moet terugvegen.
   */
  const sluitRef = useRef(sluit)
  sluitRef.current = sluit
  const geduwd = useRef(false)
  useEffect(() => {
    if (!geduwd.current) {
      history.pushState({ ...(history.state ?? {}), kiezer: true }, '')
      geduwd.current = true
    }
    const weg = () => sluitRef.current()
    window.addEventListener('popstate', weg)
    return () => window.removeEventListener('popstate', weg)
  }, [])

  const dicht = () => history.back()

  return (
    <div className="kiezer" role="dialog" aria-label={titel}>
      <Kop titel={titel} terugLabel={terugLabel} opTerug={dicht} />
      {zoekbaar && (
        <div className="kiezer-zoek">
          <input type="search" placeholder="Zoek op naam" value={filter} onChange={(e) => setFilter(e.target.value)} />
        </div>
      )}
      <ul className="lijst">
        {zichtbaar.map((o) => (
          <li key={o.waarde}>
            <button
              className="lijst-rij kiezer-rij"
              disabled={!!o.uit}
              onClick={() => { kies(o.waarde); dicht() }}
            >
              <span className="kiezer-vink">{gekozen === o.waarde && <Check size={26} strokeWidth={2} />}</span>
              <span className="kiezer-tekst">
                <span>{o.label}</span>
                {(o.uit || o.uitleg) && <span className="klein zacht">{o.uit ?? o.uitleg}</span>}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {zichtbaar.length === 0 && <Leeg>{leegTekst ?? 'Niets om uit te kiezen.'}</Leeg>}
    </div>
  )
}

/**
 * Een vraag om een reden (terugzetten, afkeuren, buiten de route). Als
 * venster over het scherm, met het toetsenbord meteen open.
 */
export function Vraag({ titel, uitleg, label, verplicht, knop, gevaar, sluit, doe }: {
  titel: string
  uitleg?: ReactNode
  label: string
  verplicht?: boolean
  knop: string
  gevaar?: boolean
  sluit: () => void
  doe: (tekst: string) => Promise<void> | void
}) {
  const [tekst, setTekst] = useState('')
  const [bezig, setBezig] = useState(false)
  const veld = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { veld.current?.focus() }, [])

  return (
    <div className="vraag-achter" onClick={() => !bezig && sluit()}>
      <div className="vraag" role="dialog" aria-label={titel} onClick={(e) => e.stopPropagation()}>
        <div className="vraag-kop">
          <h2>{titel}</h2>
          <button className="kop-knop" onClick={sluit} disabled={bezig} aria-label="Sluiten"><X size={22} /></button>
        </div>
        {uitleg && <div className="vraag-uitleg">{uitleg}</div>}
        <label className="veld-label">{label}{verplicht ? '' : ' (mag leeg)'}</label>
        <textarea ref={veld} rows={3} value={tekst} onChange={(e) => setTekst(e.target.value)} maxLength={1000} />
        <button
          className={`knop breed ${gevaar ? 'gevaar' : 'blauw'}`}
          disabled={bezig || (verplicht && !tekst.trim())}
          onClick={async () => {
            setBezig(true)
            try { await doe(tekst.trim()) } finally { setBezig(false) }
          }}
        >
          {bezig ? 'Bezig…' : knop}
        </button>
      </div>
    </div>
  )
}

export function Meldingen() {
  const melding = useStaat((s) => s.melding)
  if (!melding) return null
  return (
    <div className={`melding melding-${melding.soort}`} role={melding.soort === 'fout' ? 'alert' : 'status'} onClick={wisMelding}>
      {melding.tekst}
    </div>
  )
}

/** Het merk: zwart, schuin afgesneden, met TW10 erin. */
export function Merk({ klein }: { klein?: boolean }) {
  return (
    <div className={`merk ${klein ? 'merk-klein' : ''}`} aria-label="TW10">
      <span className="merk-tw">tw</span><span className="merk-10">10</span>
      {!klein && <span className="merk-sub">facturen</span>}
    </div>
  )
}
